<template>
    <anonymous-app v-if="isAnonymous" />
    <template v-else>
        <winck-app v-if="!hasHeylohaBranding()" />
        <heyloha-app v-if="hasHeylohaBranding()" />
    </template>
</template>

<script setup lang="ts">
    import { computed, watch } from 'vue';
    import AnonymousApp from '@/shared/components/app/AnonymousApp.vue';
    import WinckApp from '@/shared/components/app/WinckApp.vue';
    import HeylohaApp from '@/shared/components/app/HeylohaApp.vue';
    import useAppBranding from '@/services/app-branding/composables/useAppBranding';
    import { useI18n } from 'vue-i18n';
    import { useRouter } from 'vue-router';
    import useEventBus from '@/services/eventbus/composables/useEventBus';
    import MercureConnectionLostEvent, { type MercureConnectionLostEventParameters } from '@/services/keyvue-api/mercure/MercureConnectionLostEvent';
    import { useNotify } from '@keyvue/one-ui';
    import LostConnectionNotification from '@/services/keyvue-api/components/LostConnectionNotification.vue';
    import useClient from '@/services/keyvue-api/composables/useClient';
    import MercureMessageEvent from '@/services/keyvue-api/mercure/MercureMessageEvent';
    import NewHandoverNotification from '@/ai/assistent/components/notification/NewHandoverNotification.vue';
    import { Chat, GenerateCompanyTokenCommand } from '@keyvue/one-api-client';
    import MercureConnection from '@/services/keyvue-api/mercure/MercureConnection';

    const router = useRouter();
    const { hasHeylohaBranding } = useAppBranding();
    const { mergeLocaleMessage, locale } = useI18n();
    const eventBus = useEventBus();
    const { notify } = useNotify();
    const { client } = useClient();

    const companyMercureConnection = MercureConnection.getInstance('company');
    companyMercureConnection.setTokenCallback(async () => {
        if (!client.session.companyId) {
            throw new Error('failed to establish connection with company');
        }

        const response = await client.dispatch(new GenerateCompanyTokenCommand(client.session.companyId));

        return response;
    });

    eventBus.subscribe<MercureConnectionLostEventParameters>(MercureConnectionLostEvent('company'), (event) => {
        if (!client.session.isLoggedIn) {
            return;
        }

        notify({
            component: LostConnectionNotification,
            props: {
                title: 'De realtime verbinding is verloren',
                text: 'Klik hieronder om de verbinding te herstellen',
                connectionName: event.connectionName,
            },
            duration: 0,
        });
    });

    eventBus.subscribe(MercureMessageEvent('company', 'chat.requires-handover'), (event: any) => {
        if (!client.session.isLoggedIn) {
            return;
        }

        const chat = Chat.fromObject(event);
        const { notifiableUsers } = chat.assistent.handoverSettings;
        const currentUserNeedsNotification = notifiableUsers.some((user) => (user.id.equals(client.session.userId)));

        if (!currentUserNeedsNotification) {
            return;
        }

        notify({
            component: NewHandoverNotification,
            props: {
                title: 'Nieuw chatverzoek',
                text: 'Er wacht een gebruiker op persoonlijk contact',
                assistentId: chat.assistent.id.id,
                chatId: chat.id.id,
            },
            duration: 0,
        });
    });

    eventBus.subscribe(MercureMessageEvent('company', 'chat.handover-referred'), (event: any) => {
        if (!client.session.isLoggedIn) {
            return;
        }

        const chat = Chat.fromObject(event);
        const currentUserNeedsNotification = chat.handoverReferredTo?.id.equals(client.session.userId);

        if (!currentUserNeedsNotification) {
            return;
        }

        notify({
            component: NewHandoverNotification,
            props: {
                title: 'Gesprek toegewezen',
                text: 'Er is een gesprek aan jou toegewezen',
                assistentId: chat.assistent.id.id,
                chatId: chat.id.id,
            },
            duration: 0,
        });
    });

    if (hasHeylohaBranding()) {
        mergeLocaleMessage('nl', {
            application: {
                name: 'Heyloha',
            },
            domain: {
                brand: 'Website',
                brand_inline: 'De @.lower:domain.brand',
                brands: 'Websites',
                brands_inline: 'De @.lower:domain.brands',
            },
        });

        mergeLocaleMessage('en', {
            application: {
                name: 'Heyloha',
            },
            domain: {
                brand: 'Website',
                brand_inline: 'The @.lower:domain.brand',
                brands: 'Websites',
                brands_inline: 'The @.lower:domain.brands',
            },
        });
    }

    const isAnonymous = computed(() => {
        return router.currentRoute.value.meta.anonymous;
    });

    watch(locale, (value) => {
        document.documentElement.lang = value;
    }, { immediate: true });
</script>

<style lang="scss">
body.branding--WINCK {
    #app,
    #sidesheets,
    #dialogs,
    #collapses,
    #interaction-blockers,
    #notifications,
    #tooltips {
        font-family: 'Nunito Sans', sans-serif;

        h1, h2, h3, h4, h5, h6 {
            font-family: 'PT Sans', sans-serif;
        }
    }
}

body.branding--HEYLOHA {
    #app,
    #sidesheets,
    #dialogs,
    #collapses,
    #interaction-blockers,
    #notifications,
    #tooltips {
        font-family: 'Roboto', sans-serif;
    }
}

html, body, #app {
    height: 100%;
    overflow: hidden;
    line-height: unset;
}

section {
    width: 100%;
}

a {
    text-decoration: none;
}

button {
    cursor: pointer;
}

#warnings > *:last-child {
    margin-bottom: size(lg);
}
</style>
